import { useQuery } from "@tanstack/react-query";
import { transactionApi } from "../../api";
import type { StripeFrontendConfigResponse } from "../../types/generated";
import { paymentQueryKeys } from "./paymentQueryKeys";

/**
 * Frontend-safe Stripe config for a facility direct charge
 * (GET /transactions/facilities/{id}/frontend-config).
 *
 * This connected-account configuration is specifically for authenticating a
 * facility PaymentIntent. Platform-wallet SetupIntents use the separate wallet
 * hook, because confirming either intent in the other account fails.
 *
 * Shared spine (promoted out of modules/booking into @bw/shared) so the court
 * booking, class-enrollment, and tournament-registration paths all load Elements
 * from ONE authoritative hook. Behaviour is byte-identical to the old
 * booking-local hook: gated on a truthy facilityId AND the caller's `enabled`
 * flag, with `retry: false` so a 503 surfaces immediately.
 */
export function useFrontendConfig(facilityId: string | undefined, enabled = true) {
  return useQuery<StripeFrontendConfigResponse>({
    queryKey: paymentQueryKeys.frontendConfig(facilityId ?? ""),
    queryFn: () => transactionApi.getFrontendConfig(facilityId as string),
    enabled: Boolean(facilityId) && enabled,
    retry: false,
  });
}
